/**
 * The controls the course list uses for selecting, deleting (archiving) and
 * restoring courses: a button, the checkbox glyph, the Active | Archived
 * switch, the sticky selection bar and the undo notice.
 *
 * Contrast, measured (WCAG 2.x):
 *   white on danger #C0392B 5.44 · pressed #A93226 6.62
 *   white on tealDark 5.23 · brownDark on white 15.6
 *   brownDark on the teal page 5.59 (the selected-card border; needs 3:1)
 *   yellow #FACF47 on brownDark 10.46 (Undo on the notice)
 * White text on the page teal is 2.79 — under AA — so nothing here puts
 * text straight on the teal: header controls sit on white.
 */
import React, {useState} from 'react';
import {Pressable, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '../../../Atoms/CCText';
import {F, S, STATUS, T} from '../courseTheme';
import {E} from '../editor/editorTheme';
import {focusVisible, motion, ring} from '../editor/ui';

export type ListButtonVariant = 'danger' | 'accent' | 'secondary';

/**
 * The shared focus ring is tealDark, which is 1.8:1 against the page teal —
 * invisible where it matters. Controls that sit on the teal draw it in
 * brownDark instead (5.59:1 against the teal, 15.6:1 against white).
 */
const pageRing = (on: boolean, onTeal?: boolean) =>
  onTeal ? ({...ring(on), outlineColor: F.brownDark} as any) : ring(on);

const VARIANTS: Record<
  ListButtonVariant,
  {bg: string; pressed: string; hover: string; fg: string; border: string}
> = {
  // Destructive, so red — but a calm, solid red with a sentence-case label,
  // never a shouting one: the action is undoable.
  danger: {
    bg: STATUS.bad.fg,
    pressed: '#A93226',
    hover: '#B33427',
    fg: F.white,
    border: STATUS.bad.fg,
  },
  accent: {
    bg: F.tealDark,
    pressed: '#225F58',
    hover: '#276C64',
    fg: F.white,
    border: F.tealDark,
  },
  secondary: {
    bg: F.white,
    pressed: '#ECEBEA',
    hover: '#F5F4F3',
    fg: F.brownDark,
    border: 'rgba(38,35,34,0.22)',
  },
};

const TOGGLED_ON = {
  bg: F.brownDark,
  pressed: '#3A3634',
  hover: '#33302E',
  fg: F.white,
  border: F.brownDark,
};

/**
 * A 44px button with a visible keyboard focus ring and a pressed state.
 * `pressedOn` makes it a toggle (aria-pressed), drawn filled when on.
 */
export function ListButton({
  label,
  onPress,
  icon,
  variant = 'secondary',
  disabled = false,
  pressedOn,
  a11yLabel,
  a11yHint,
  reduced,
  buttonRef,
  onTeal,
}: {
  label: string;
  onPress: () => void;
  icon?: string;
  variant?: ListButtonVariant;
  disabled?: boolean;
  pressedOn?: boolean;
  a11yLabel?: string;
  a11yHint?: string;
  reduced: boolean;
  buttonRef?: React.Ref<any>;
  /** Sits on the page teal: draw the focus ring dark. */
  onTeal?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const toggle = typeof pressedOn === 'boolean';
  const on = toggle && pressedOn;
  // On: filled dark, like the chosen half of the view switch — 5.59:1 against
  // the page teal, where a teal fill would all but vanish (1.8:1).
  const t = on ? TOGGLED_ON : VARIANTS[variant];
  return (
    <Pressable
      ref={buttonRef}
      accessibilityRole="button"
      accessibilityLabel={a11yLabel ?? label}
      accessibilityHint={a11yHint}
      accessibilityState={{disabled}}
      // react-native-web 0.19 drops accessibilityState; only aria-* reaches
      // the DOM.
      {...((toggle ? {'aria-pressed': on} : {}) as any)}
      disabled={disabled}
      onPress={() => {
        if (!disabled) onPress();
      }}
      onFocus={() => setFocused(focusVisible())}
      onBlur={() => setFocused(false)}
      style={({pressed, hovered}: any) => [
        {
          minHeight: 44,
          minWidth: 44,
          paddingHorizontal: S[4],
          borderRadius: F.radius.button,
          borderWidth: 1,
          borderColor: t.border,
          backgroundColor: disabled ? t.bg : pressed ? t.pressed : hovered ? t.hover : t.bg,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: S[2],
          // 0.45: inside the 0.38–0.5 band, and the cursor says so too.
          opacity: disabled ? 0.45 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        } as any,
        motion(reduced),
        pageRing(focused, onTeal),
      ]}>
      {icon ? <Ionicons name={icon} size={18} color={t.fg} /> : null}
      <CCText style={{fontSize: T.sm + 1, fontWeight: '600', color: t.fg, flexShrink: 1}}>
        {label}
      </CCText>
    </Pressable>
  );
}

/**
 * The checkbox glyph. Unticked is a white box with a dark border, ticked is
 * filled teal with a white tick, and partly ticked shows a bar: the state is
 * the shape, not only the colour. Decorative — the control around it carries
 * the role and aria-checked.
 */
export function CheckMark({
  state,
  size = 24,
}: {
  state: boolean | 'mixed';
  size?: number;
}) {
  const filled = state !== false;
  return (
    <View
      accessible={false}
      importantForAccessibility="no-hide-descendants"
      style={{
        width: size,
        height: size,
        borderRadius: 6,
        borderWidth: 2,
        borderColor: filled ? F.white : F.brownDark,
        backgroundColor: filled ? F.tealDark : F.white,
        alignItems: 'center',
        justifyContent: 'center',
        // A ring of white around a filled box, so it reads on dark artwork.
        shadowColor: '#000',
        shadowOpacity: 0.25,
        shadowRadius: 2,
        shadowOffset: {width: 0, height: 1},
      }}>
      {state === true ? (
        <Ionicons name="checkmark" size={size - 6} color={F.white} />
      ) : state === 'mixed' ? (
        <Ionicons name="remove" size={size - 6} color={F.white} />
      ) : null}
    </View>
  );
}

/** "Select all": a real checkbox, partly ticked when some cards are. */
export function SelectAll({
  count,
  selected,
  onPress,
  reduced,
}: {
  count: number;
  selected: number;
  onPress: () => void;
  reduced: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const state: boolean | 'mixed' =
    count > 0 && selected >= count ? true : selected > 0 ? 'mixed' : false;
  return (
    <Pressable
      accessibilityRole="checkbox"
      accessibilityLabel={`Select all ${count} ${count === 1 ? 'course' : 'courses'}`}
      accessibilityState={{checked: state}}
      {...({'aria-checked': state} as any)}
      onPress={onPress}
      onFocus={() => setFocused(focusVisible())}
      onBlur={() => setFocused(false)}
      style={({pressed, hovered}: any) => [
        {
          minHeight: 44,
          paddingHorizontal: S[3],
          borderRadius: F.radius.button,
          borderWidth: 1,
          borderColor: 'rgba(38,35,34,0.22)',
          backgroundColor: pressed ? '#ECEBEA' : hovered ? '#F5F4F3' : F.white,
          flexDirection: 'row',
          alignItems: 'center',
          gap: S[2],
          cursor: 'pointer',
        } as any,
        motion(reduced),
        pageRing(focused, true),
      ]}>
      <CheckMark state={state} size={20} />
      <CCText style={{fontSize: T.sm + 1, fontWeight: '600', color: F.brownDark}}>
        Select all
      </CCText>
    </Pressable>
  );
}

export type ListView = 'active' | 'archived';

const SEGMENTS: Array<{value: ListView; word: string; icon: string}> = [
  {value: 'active', word: 'Active', icon: 'albums-outline'},
  {value: 'archived', word: 'Archived', icon: 'archive-outline'},
];

/**
 * Active | Archived. A radio group: it filters the one list below rather
 * than switching between screens. The chosen segment is filled dark with
 * white text (15.6:1) and the other white with dark text, so which one is on
 * is a light/dark difference, not a hue.
 */
export function ViewSwitch({
  value,
  onChange,
  reduced,
}: {
  value: ListView;
  onChange: (next: ListView) => void;
  reduced: boolean;
}) {
  const [focused, setFocused] = useState<ListView | null>(null);
  return (
    <View
      accessibilityRole="radiogroup"
      accessibilityLabel="Which courses to show"
      style={{
        flexDirection: 'row',
        backgroundColor: F.white,
        borderRadius: F.radius.button + 2,
        padding: 2,
        gap: 2,
      }}>
      {SEGMENTS.map(seg => {
        const on = seg.value === value;
        return (
          <Pressable
            key={seg.value}
            accessibilityRole="radio"
            accessibilityLabel={`${seg.word} courses`}
            accessibilityState={{checked: on}}
            {...({'aria-checked': on} as any)}
            onPress={() => {
              if (!on) onChange(seg.value);
            }}
            onFocus={() => setFocused(focusVisible() ? seg.value : null)}
            onBlur={() => setFocused(null)}
            style={({pressed, hovered}: any) => [
              {
                minHeight: 40,
                minWidth: 44,
                paddingHorizontal: S[3],
                borderRadius: F.radius.button,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                backgroundColor: on
                  ? F.brownDark
                  : pressed
                  ? '#ECEBEA'
                  : hovered
                  ? '#F5F4F3'
                  : F.white,
                cursor: on ? 'default' : 'pointer',
              } as any,
              motion(reduced),
              ring(focused === seg.value),
            ]}>
            <Ionicons name={seg.icon} size={16} color={on ? F.white : F.brownDark} />
            <CCText
              style={{
                fontSize: T.sm + 1,
                fontWeight: on ? '700' : '600',
                color: on ? F.white : F.brownDark,
              }}>
              {seg.word}
            </CCText>
          </Pressable>
        );
      })}
    </View>
  );
}

/**
 * The bar along the bottom in select mode: how many are selected, the one
 * action, and a way out. A sibling below the scrolling list rather than an
 * overlay on it, so it never hides the last row of cards.
 */
export function SelectionBar({
  count,
  summary,
  actionLabel,
  actionIcon,
  variant,
  busy,
  onAction,
  onCancel,
  reduced,
  actionRef,
}: {
  count: number;
  summary: string;
  actionLabel: string;
  actionIcon: string;
  variant: ListButtonVariant;
  busy: boolean;
  onAction: () => void;
  onCancel: () => void;
  reduced: boolean;
  actionRef?: React.Ref<any>;
}) {
  return (
    <View
      testID="selection-bar"
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: S[3],
        paddingHorizontal: S[6],
        paddingVertical: S[3],
        backgroundColor: F.white,
        borderTopWidth: 1,
        borderTopColor: E.line,
        shadowColor: '#000',
        shadowOffset: {width: 0, height: -6},
        shadowOpacity: 0.1,
        shadowRadius: 16,
        elevation: 8,
      }}>
      <CCText
        accessibilityLiveRegion="polite"
        style={{fontSize: T.base, fontWeight: '700', color: F.brownDark}}>
        {summary}
      </CCText>
      <View style={{flexDirection: 'row', gap: S[2], flexWrap: 'wrap'}}>
        <ListButton label="Cancel" onPress={onCancel} reduced={reduced} />
        <ListButton
          buttonRef={actionRef}
          label={actionLabel}
          icon={actionIcon}
          variant={variant}
          disabled={count === 0 || busy}
          onPress={onAction}
          reduced={reduced}
        />
      </View>
    </View>
  );
}

/**
 * A brief notice of what just happened, with Undo when it can be undone.
 * Announced politely and never focused: it must not pull a keyboard user
 * away from where they were.
 */
export function ListToast({
  text,
  tone = 'info',
  onUndo,
  undoing,
  onDismiss,
  reduced,
}: {
  text: string;
  tone?: 'info' | 'bad';
  onUndo?: () => void;
  undoing?: boolean;
  onDismiss: () => void;
  reduced: boolean;
}) {
  const [focused, setFocused] = useState<'undo' | 'close' | null>(null);
  return (
    <View
      pointerEvents="box-none"
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: S[6],
        alignItems: 'center',
        paddingHorizontal: S[4],
      }}>
      <View
        accessibilityLiveRegion="polite"
        {...({role: tone === 'bad' ? 'alert' : 'status'} as any)}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: S[2],
          maxWidth: 560,
          width: '100%',
          minHeight: 52,
          paddingLeft: S[4],
          paddingRight: S[1],
          borderRadius: 12,
          backgroundColor: F.brownDark,
          shadowColor: '#000',
          shadowOffset: {width: 0, height: 8},
          shadowOpacity: 0.2,
          shadowRadius: 24,
          elevation: 10,
        }}>
        <Ionicons
          name={tone === 'bad' ? 'alert-circle-outline' : 'checkmark-circle-outline'}
          size={20}
          color={tone === 'bad' ? '#F5A3A3' : '#8FD3C9'}
        />
        <CCText
          style={{flex: 1, fontSize: T.base - 1, fontWeight: '600', color: F.white, lineHeight: 20}}>
          {text}
        </CCText>
        {onUndo ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={undoing ? 'Undo, restoring' : 'Undo'}
            accessibilityState={{disabled: !!undoing}}
            disabled={!!undoing}
            onPress={() => {
              if (!undoing) onUndo();
            }}
            onFocus={() => setFocused(focusVisible() ? 'undo' : null)}
            onBlur={() => setFocused(null)}
            style={({pressed, hovered}: any) => [
              {
                minHeight: 44,
                minWidth: 44,
                paddingHorizontal: S[3],
                borderRadius: 8,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: pressed
                  ? 'rgba(255,255,255,0.16)'
                  : hovered
                  ? 'rgba(255,255,255,0.08)'
                  : 'transparent',
                opacity: undoing ? 0.6 : 1,
                cursor: undoing ? 'progress' : 'pointer',
              } as any,
              motion(reduced),
              ring(focused === 'undo'),
            ]}>
            <CCText style={{fontSize: T.base - 1, fontWeight: '700', color: F.yellow}}>
              {undoing ? 'Restoring…' : 'Undo'}
            </CCText>
          </Pressable>
        ) : null}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Dismiss"
          onPress={onDismiss}
          onFocus={() => setFocused(focusVisible() ? 'close' : null)}
          onBlur={() => setFocused(null)}
          style={({pressed, hovered}: any) => [
            {
              width: 44,
              height: 44,
              borderRadius: 22,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: pressed
                ? 'rgba(255,255,255,0.16)'
                : hovered
                ? 'rgba(255,255,255,0.08)'
                : 'transparent',
              cursor: 'pointer',
            } as any,
            motion(reduced),
            ring(focused === 'close'),
          ]}>
          <Ionicons name="close" size={20} color={F.white} />
        </Pressable>
      </View>
    </View>
  );
}
